import React, { useState, useEffect, useCallback } from 'react';
import { getReglaById, createRegla, updateRegla } from '../services/reglaCreditoService';
import { getLineasCredito } from '../services/lineaCreditoService';
import { getMonedas } from '../services/monedaService';
import { getCalificacionesRiesgo } from '../services/calificacionRiesgoService';
import { getNivelesFidelidad } from '../services/nivelFidelidadService';
import { getCategorias } from '../services/categoriaService';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, ArrowLeft, RefreshCw, ShieldCheck, DollarSign, Clock, Percent, ListFilter, Calculator, AlertCircle } from 'lucide-react';
import { toast, Toaster } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const ReglaCreditoForm = () => {
    const { id } = useParams();
    const isEditMode = !!id;
    const navigate = useNavigate();
    const { token } = useAuth();

    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(true);

    // Listas para selects
    const [catalogos, setCatalogos] = useState({
        lineas: [],
        monedas: [],
        riesgos: [],
        fidelidades: [],
        categorias: []
    });

    const [form, setForm] = useState({
        lineaCredito: { id: '' },
        moneda: { id: '' },
        montoDesde: 0,
        montoHasta: 0,
        plazoMinimo: 6,
        plazoMaximo: 24,
        plazoIncremento: 6,
        tasaCompensatoria: 0,
        tasaPunitoria: 0,
        calificacionRiesgo: { id: '' },
        nivelFidelidad: { id: '' },
        categoria: { id: '' },
        antiguedadMinima: 0,
        montoIncremento: 500000
    });

    const cargarDatos = useCallback(async () => {
        try {
            setFetching(true);
            const [lineas, monedas, riesgos, fidelidades, categorias] = await Promise.all([
                getLineasCredito(token),
                getMonedas(token),
                getCalificacionesRiesgo(token),
                getNivelesFidelidad(token),
                getCategorias(token)
            ]);

            setCatalogos({ lineas, monedas, riesgos, fidelidades, categorias });

            if (isEditMode) {
                const data = await getReglaById(token, id);
                setForm({
                    ...data,
                    lineaCredito: { id: data.lineaCredito?.id || '' },
                    moneda: { id: data.moneda?.id || '' },
                    calificacionRiesgo: { id: data.calificacionRiesgo?.id || '' },
                    nivelFidelidad: { id: data.nivelFidelidad?.id || '' },
                    categoria: { id: data.categoria?.id || '' }
                });
            }
        } catch (error) {
            toast.error('Error al sincronizar catálogos');
            console.error(error);
        } finally {
            setFetching(false);
        }
    }, [id, isEditMode, token]);

    useEffect(() => {
        cargarDatos();
    }, [cargarDatos]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validaciones básicas
        if (!form.lineaCredito.id || !form.moneda.id) {
            toast.error('Línea y Moneda son campos obligatorios');
            return;
        }

        try {
            setLoading(true);
            const payload = {
                ...form,
                lineaCredito: { id: parseInt(form.lineaCredito.id) },
                moneda: { id: parseInt(form.moneda.id) },
                calificacionRiesgo: form.calificacionRiesgo.id ? { id: parseInt(form.calificacionRiesgo.id) } : null,
                nivelFidelidad: form.nivelFidelidad.id ? { id: parseInt(form.nivelFidelidad.id) } : null,
                categoria: form.categoria.id ? { id: parseInt(form.categoria.id) } : null
            };

            if (isEditMode) {
                await updateRegla(token, id, payload);
                toast.success('Regla actualizada');
            } else {
                await createRegla(token, payload);
                toast.success('Nueva regla definida');
            }
            setTimeout(() => navigate('/parametrizacion/reglas-credito'), 1000);
        } catch (error) {
            toast.error('Error al guardar la regla');
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
                <RefreshCw className="w-10 h-10 animate-spin text-blue-600" />
                <p className="text-slate-500 font-bold uppercase text-xs tracking-widest italic tracking-wider">Cargando parámetros del Product Factory...</p>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto bg-white shadow-2xl border border-slate-300 mb-10 overflow-hidden rounded-none animate-in fade-in duration-700">
            <Toaster position="top-right" />

            {/* Header Enterprise */}
            <div className="bg-slate-700 p-4 flex items-center justify-between text-white border-b border-slate-800">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate('/parametrizacion/reglas-credito')} className="p-1.5 hover:bg-slate-600 transition-colors text-slate-300 hover:text-white">
                        <ArrowLeft className="w-6 h-6" />
                    </button>
                    <div>
                        <h1 className="text-lg font-black tracking-tight uppercase italic flex items-center gap-2">
                            <ShieldCheck className="w-5 h-5 text-blue-400" />
                            {isEditMode ? 'Ajuste de Regla de Negocio' : 'Definición de Regla Product Factory'}
                        </h1>
                        <p className="text-[9px] text-slate-400 font-bold uppercase tracking-[0.3em]">Gestor de Políticas Crediticias de la Entidad</p>
                    </div>
                </div>
                <div className="hidden md:block bg-slate-600 px-3 py-1 text-[10px] font-black uppercase border border-slate-500 shadow-sm italic">PR-RULES-v2.5</div>
            </div>

            <form onSubmit={handleSubmit} className="p-6 bg-[#f1f5f9]/50">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                    {/* COL 1: Identificación y Segmentación */}
                    <div className="space-y-6">
                        <div className="bg-white border border-slate-200 p-5 shadow-sm relative overflow-hidden h-full">
                            <div className="absolute top-0 left-0 w-1.5 h-full bg-blue-600"></div>
                            <h3 className="text-[11px] font-black text-slate-700 uppercase tracking-widest mb-4 flex items-center gap-2 border-b border-slate-100 pb-2">
                                <ListFilter className="w-4 h-4 text-blue-500" />
                                Segmentación y Producto
                            </h3>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-[10px] font-black text-slate-500 mb-1.5 uppercase tracking-tight">Línea de Crédito <span className="text-red-500">*</span></label>
                                    <select
                                        required
                                        className="w-full px-3 py-2.5 border border-slate-300 focus:border-blue-600 outline-none font-bold uppercase text-[12px] rounded-none bg-slate-50 focus:bg-white"
                                        value={form.lineaCredito.id}
                                        onChange={(e) => setForm({ ...form, lineaCredito: { id: e.target.value } })}
                                    >
                                        <option value="">-- SELECCIONAR LÍNEA --</option>
                                        {catalogos.lineas.map(l => <option key={l.id} value={l.id}>{l.nombre}</option>)}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-[10px] font-black text-slate-500 mb-1.5 uppercase tracking-tight">Moneda de Desembolso <span className="text-red-500">*</span></label>
                                    <select
                                        required
                                        className="w-full px-3 py-2.5 border border-slate-300 focus:border-blue-600 outline-none font-bold uppercase text-[12px] rounded-none bg-slate-50 focus:bg-white"
                                        value={form.moneda.id}
                                        onChange={(e) => setForm({ ...form, moneda: { id: e.target.value } })}
                                    >
                                        <option value="">-- SELECCIONAR MONEDA --</option>
                                        {catalogos.monedas.map(m => <option key={m.id} value={m.id}>{m.nombre} ({m.simbolo})</option>)}
                                    </select>
                                </div>

                                <div className="pt-4 border-t border-dashed border-slate-200">
                                    <p className="text-[9px] font-black text-slate-400 uppercase mb-3 italic">Población Objetivo (Socio)</p>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-[10px] font-black text-slate-500 mb-1.5 uppercase tracking-tight text-red-700">Calificación de Riesgo</label>
                                            <select
                                                className="w-full px-3 py-2 border border-slate-300 focus:border-blue-600 outline-none font-bold uppercase text-[11px] rounded-none shadow-sm"
                                                value={form.calificacionRiesgo.id}
                                                onChange={(e) => setForm({ ...form, calificacionRiesgo: { id: e.target.value } })}
                                            >
                                                <option value="">NO APLICA (CUALQUIERA)</option>
                                                {catalogos.riesgos.map(r => <option key={r.id} value={r.id}>{r.codigo} - {r.descripcion}</option>)}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-[10px] font-black text-slate-500 mb-1.5 uppercase tracking-tight text-blue-700">Nivel de Fidelidad</label>
                                            <select
                                                className="w-full px-3 py-2 border border-slate-300 focus:border-blue-600 outline-none font-bold uppercase text-[11px] rounded-none shadow-sm"
                                                value={form.nivelFidelidad.id}
                                                onChange={(e) => setForm({ ...form, nivelFidelidad: { id: e.target.value } })}
                                            >
                                                <option value="">NO APLICA (CUALQUIERA)</option>
                                                {catalogos.fidelidades.map(f => <option key={f.id} value={f.id}>{f.codigo} - {f.descripcion}</option>)}
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-[10px] font-black text-slate-500 mb-1.5 uppercase tracking-tight text-teal-700">Categoría de Socio</label>
                                            <select
                                                className="w-full px-3 py-2 border border-slate-300 focus:border-blue-600 outline-none font-bold uppercase text-[11px] rounded-none shadow-sm"
                                                value={form.categoria.id}
                                                onChange={(e) => setForm({ ...form, categoria: { id: e.target.value } })}
                                            >
                                                <option value="">NO APLICA (CUALQUIERA)</option>
                                                {catalogos.categorias.map(c => <option key={c.id} value={c.id}>{c.nombreCategoria}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* COL 2: Montos y Plazos */}
                    <div className="space-y-6">
                        <div className="bg-white border border-slate-200 p-5 shadow-sm relative overflow-hidden h-full">
                            <div className="absolute top-0 left-0 w-1.5 h-full bg-slate-500"></div>
                            <h3 className="text-[11px] font-black text-slate-700 uppercase tracking-widest mb-4 flex items-center gap-2 border-b border-slate-100 pb-2">
                                <Calculator className="w-4 h-4 text-slate-400" />
                                Montos y Plazos de Crédito
                            </h3>
                            <div className="grid grid-cols-2 gap-4 uppercase font-bold text-[11px]">
                                <div className="col-span-2 grid grid-cols-2 gap-3 p-3 bg-gray-50 border border-gray-200">
                                    <div>
                                        <label className="block text-[10px] font-black text-gray-500 mb-1.5 tracking-tighter uppercase">Monto Mínimo</label>
                                        <div className="relative">
                                            <input type="number" step="0.01" className="w-full pl-8 pr-3 py-2 border border-gray-300 font-bold focus:border-blue-500 outline-none" value={form.montoDesde} onChange={(e) => setForm({ ...form, montoDesde: e.target.value })} />
                                            <DollarSign className="absolute left-2 top-2.5 w-3.5 h-3.5 text-gray-400" />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-black text-gray-500 mb-1.5 tracking-tighter uppercase">Monto Máximo</label>
                                        <div className="relative">
                                            <input type="number" step="0.01" className="w-full pl-8 pr-3 py-2 border border-blue-200 font-bold focus:border-blue-500 outline-none bg-blue-50/10" value={form.montoHasta} onChange={(e) => setForm({ ...form, montoHasta: e.target.value })} />
                                            <DollarSign className="absolute left-2 top-2.5 w-3.5 h-3.5 text-blue-400" />
                                        </div>
                                    </div>
                                    <div className="col-span-2 pt-2 border-t border-gray-200 mt-1">
                                        <label className="block text-[10px] font-black text-gray-400 mb-1.5 tracking-tighter uppercase italic">Incremento Sugerido (Monto)</label>
                                        <div className="relative">
                                            <input type="number" step="0.01" className="w-full pl-8 pr-3 py-2 border border-gray-300 font-bold focus:border-blue-500 outline-none rounded-none" value={form.montoIncremento} onChange={(e) => setForm({ ...form, montoIncremento: e.target.value })} />
                                            <DollarSign className="absolute left-2 top-2.5 w-3.5 h-3.5 text-gray-300" />
                                        </div>
                                    </div>
                                </div>
                                <div className="space-y-4 col-span-2">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-[10px] font-black text-gray-500 mb-1.5 tracking-tighter uppercase">Plazo Mínimo (Meses)</label>
                                            <input type="number" className="w-full px-3 py-2 border border-gray-300 font-bold" value={form.plazoMinimo} onChange={(e) => setForm({ ...form, plazoMinimo: e.target.value })} />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-black text-gray-500 mb-1.5 tracking-tighter uppercase">Plazo Máximo (Meses)</label>
                                            <input type="number" className="w-full px-3 py-2 border border-blue-200 font-bold" value={form.plazoMaximo} onChange={(e) => setForm({ ...form, plazoMaximo: e.target.value })} />
                                        </div>
                                    </div>
                                    <div className="pt-2">
                                        <label className="block text-[10px] font-black text-gray-400 mb-1.5 tracking-tighter uppercase italic">Incremento de Plazo (Meses)</label>
                                        <input type="number" className="w-full px-3 py-2 border border-gray-300 font-bold" value={form.plazoIncremento} onChange={(e) => setForm({ ...form, plazoIncremento: e.target.value })} />
                                    </div>
                                </div>
                                <div className="col-span-2 pt-4 border-t border-gray-200 grid grid-cols-2 gap-3 pb-2 uppercase text-[11px]">
                                    <div>
                                        <label className="block text-[10px] font-black text-gray-700 mb-1.5 flex items-center gap-1 uppercase">Tasa Compensatoria (%)</label>
                                        <input type="number" step="0.01" className="w-full px-3 py-2 border border-gray-300 font-black text-gray-900 outline-none focus:border-blue-500 bg-gray-50" value={form.tasaCompensatoria} onChange={(e) => setForm({ ...form, tasaCompensatoria: e.target.value })} />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-black text-red-700 mb-1.5 flex items-center gap-1 uppercase">Tasa Moratoria (%)</label>
                                        <input type="number" step="0.01" className="w-full px-3 py-2 border border-red-200 font-black text-red-700 outline-none focus:border-red-500 bg-red-50/20" value={form.tasaPunitoria} onChange={(e) => setForm({ ...form, tasaPunitoria: e.target.value })} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* COL 3: Otros Parámetros y Guía */}
                    <div className="space-y-6">
                        <div className="bg-white border border-slate-200 p-5 shadow-sm relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-1.5 h-full bg-slate-400"></div>
                            <h3 className="text-[11px] font-black text-slate-700 uppercase tracking-widest mb-4 flex items-center gap-2 border-b border-slate-100 pb-2">
                                <Save className="w-4 h-4 text-slate-400" />
                                Parámetros de desembolso
                            </h3>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-[10px] font-black text-slate-500 mb-1.5 uppercase tracking-tight italic">Antigüedad Mínima de Socio (Meses)</label>
                                    <input type="number" className="w-full px-3 py-2 border border-slate-300 font-bold text-[12px]" value={form.antiguedadMinima} onChange={(e) => setForm({ ...form, antiguedadMinima: e.target.value })} />
                                </div>
                                <div className="bg-slate-50 p-4 border border-dashed border-slate-200">
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                                        <p className="text-[10px] font-black text-slate-500 uppercase leading-4 italic">
                                            Asegúrese de que el rango de montos NO SE SOLAPE con otras reglas de la misma línea/moneda para evitar errores de validación en la preventa.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-[#fff9db] border border-amber-200 p-5 shadow-inner italic">
                            <h4 className="text-[10px] font-black text-amber-800 uppercase mb-2 flex items-center gap-1">
                                <ShieldCheck className="w-3.5 h-3.5" /> Control de Políticas
                            </h4>
                            <p className="text-[10px] text-amber-900 leading-relaxed font-bold uppercase tracking-tighter opacity-70">
                                Una vez consolidada la regla, esta será aplicada de forma INMEDIATA a todas las nuevas solicitudes de crédito que ingresen por el simulador web o ventanilla.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Footer Consolidación */}
                <div className="mt-8 pt-6 border-t border-slate-300 flex justify-between items-center bg-white/50 -mx-6 -mb-6 p-6">
                    <button type="button" onClick={() => navigate('/parametrizacion/reglas-credito')} className="px-8 py-2.5 border border-slate-400 text-slate-600 hover:bg-slate-100 font-black text-[11px] uppercase transition-all shadow-sm">
                        Descartar Definición
                    </button>

                    <button
                        type="submit"
                        disabled={loading}
                        className="px-16 py-3.5 bg-[#0066cc] text-white shadow-xl hover:bg-[#004d99] transition-all font-black text-[13px] uppercase tracking-[0.15em] flex items-center gap-3 border border-blue-700 shadow-blue-200 disabled:opacity-50"
                    >
                        {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                        {isEditMode ? 'Consolidar Parámetros' : 'Publicar Regla en Producción'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ReglaCreditoForm;
